import { GatsbyImage, GatsbyImageProps } from 'gatsby-plugin-image';
import React, { HTMLAttributes, VideoHTMLAttributes } from 'react';
import { AssetWrapperContainer } from './AssetWrapper.styles';
import { RadiusPropKeys } from 'src/app/styles/spacing';

interface VideoProps extends VideoHTMLAttributes<HTMLVideoElement> {
  type?: string;
}

interface ImageRawProps {
  src?: string;
  alt?: string;
  loading?: HTMLImageElement['loading'];
}

export interface AssetWrapperProps extends HTMLAttributes<HTMLDivElement> {
  maxWidth?: string | number;
  videoProps?: VideoProps;
  imageProps?: GatsbyImageProps | ImageRawProps;
  radius?: RadiusPropKeys;
  cover?: boolean;
}

const AssetWrapper = ({ maxWidth, videoProps, imageProps, radius, cover, ...rest }: AssetWrapperProps): JSX.Element => {
  const type = (() => {
    if (videoProps) return 'video';
    if ((imageProps as ImageRawProps)?.src) return 'imageRaw';
    if ((imageProps as GatsbyImageProps)?.image) return 'image';

    return undefined;
  })();

  if (!type) {
    throw new Error('AssetWrapper must have either videoProps or imageProps');
  }

  const renderAsImage = () => <GatsbyImage {...(imageProps as GatsbyImageProps)} />;
  const renderAsImageRaw = () => (
    <img
      src={(imageProps as ImageRawProps).src}
      alt={(imageProps as ImageRawProps).alt}
    />
  );
  const renderAsVideo = () => (
    <video {...videoProps}>
      <source
        data-src={videoProps.src}
        type={videoProps.type || 'video/mp4'}
      />
    </video>
  );

  const renderAsset = () => {
    if (type === 'image') return renderAsImage();
    if (type === 'imageRaw') return renderAsImageRaw();
    if (type === 'video') return renderAsVideo();
  };

  return (
    <AssetWrapperContainer
      $maxWidth={maxWidth}
      $type={type}
      $cover={cover}
      $radius={radius}
      data-cy='asset-wrapper'
      {...rest}
    >
      {renderAsset()}
    </AssetWrapperContainer>
  );
};

export default AssetWrapper;
